import { BRAND_CONFIRMATION_VALUE, CREATIVES_API_TO_FORM_MAPPING, DEFAULT_ORG, RO_IN_VALUE } from '../config/dropDownConfig';
import { isAdminRole } from '../../../../Shared/constants/userRoles';
import { EDIT_UP_ERROR_MARKER, GTM_STATUS_ENUM_TO_LABEL_MAP } from '../constants/DetailedBookingConstants';
import { BASE_EDITABLE_FIELDS, FORM_FIELDS } from '../constants/FormFieldsConstants';
import { BookingDetails, BookingFormData, ContextualNode } from '../interface/BookingFormInterface';
import { NON_CONTEXTUAL_PACKAGE_NAME } from '../../../constants/formConstants';
import { ApiError } from '../../../../Shared/interfaces/ApiError';

/**
 * Maps a booking-edit failure to a user-facing message. When the waitlist is on the backend
 * rejects increasing a segment's impressions ("edit-up"); surface a dedicated, actionable
 * message for that case and fall back to the generic edit-failure message otherwise.
 */
export const resolveEditErrorMessage = (
    error: ApiError | undefined,
    t: (key: string) => string
): string => {
    if (error?.message?.includes(EDIT_UP_ERROR_MARKER)) {
        return t('booking-edit-up-not-allowed');
    }
    return t('booking-details-edit-failure');
};

/**
 * Gets the list of editable fields based on user role
 */
export const getEditableFields = (userRole: string): string[] => {
    const editableFields: string[] = [...BASE_EDITABLE_FIELDS];

    if (isAdminRole(userRole)) {
        editableFields.push(FORM_FIELDS.GTM_STATUS);
    }

    return editableFields;
};

/**
 * Checks if any editable fields have changed between current and original data
 */
export const hasEditableFieldsChanged = (
    currentData: BookingFormData,
    originalData: BookingFormData,
    userRole: string
): boolean => {
    const editableFields = getEditableFields(userRole);

    return editableFields.some(
        (field) =>
            currentData[field as keyof BookingFormData] !==
            originalData[field as keyof BookingFormData]
    );
};

/**
 * Gets only the changed editable fields between current and original data
 */
export const getChangedEditableFields = (
    currentData: BookingFormData,
    originalData: BookingFormData,
    userRole: string
): Partial<BookingFormData> => {
    const editableFields = getEditableFields(userRole);
    const changedFields: Partial<BookingFormData> = {};

    editableFields.forEach((field) => {
        const fieldKey = field as keyof BookingFormData;
        if (currentData[fieldKey] !== originalData[fieldKey]) {
            (changedFields as any)[fieldKey] = currentData[fieldKey];
        }
    });

    return changedFields;
};

/**
 * Gets default editable form data for new edit mode
 */
export const getDefaultEditableFormData = (formData: BookingFormData): Partial<BookingFormData> => {
    return {
        org: formData.org,
        rodeoAdName: formData.rodeoAdName,
        brand: formData.brand,
        exactBillingValue: formData.exactBillingValue,
        sfOpportunityId: formData.sfOpportunityId,
        ro: formData.ro,
        commentsByPoc: formData.commentsByPoc,
        creatives: formData.creatives,
    };
};

export const mapBookingResponseToFormData =
    (booking: BookingDetails['booking']): BookingFormData => {
        const creatives = booking.creativesStage ? CREATIVES_API_TO_FORM_MAPPING[booking.creativesStage] : ''
        return {
            aeName: booking.accountExecName || '',
            amName: booking.accountManagerName || '',
            category: booking.categoryName || '',
            org: booking.orgName || DEFAULT_ORG,
            rodeoAdName: booking.rodeoAdvertiserName || '',
            brand: booking.brandName || '',
            typeOfPurchase: booking.typeOfPurchase || '',
            exactBillingValue: booking.roValue || 0,
            sfOpportunityId: booking.sfOpportunityId || '',
            ro: booking.roIn ? RO_IN_VALUE.YES : RO_IN_VALUE.NO,
            commentsByPoc: booking.commentsByPOC || '',
            creatives,
            gtmStatus: booking.gtmStatus || '',
            brandConfirmation: booking.brandConfirmation ? BRAND_CONFIRMATION_VALUE.YES : BRAND_CONFIRMATION_VALUE.NO
        };
    }

export const getProcessedValue = (
    numericFields: string[],
    field: string,
    value: string
) => numericFields.includes(field)
        ? value === ''
            ? 0
            : parseFloat(value) || 0
        : value;


export const getTranslatedGtmStatusOptions = (t: (key: string) => string) => {
    return GTM_STATUS_ENUM_TO_LABEL_MAP.map(option => ({
        id: option.value,
        value: option.value,
        label: t(option.label),
    }));
}

/**
 * Groups contextual nodes by their slot or package name.
 * For non-contextual packages, groups by contextualNode; otherwise groups by slotOrPackageName.
 */
export const buildSlotToSegmentName = (
    contextualNodes: ContextualNode[]
): Map<string, ContextualNode[]> => {
    const slotGroups = new Map<string, ContextualNode[]>();
    contextualNodes.forEach((node) => {
        const isNonContextual =
            node.slotOrPackageName?.toLowerCase() === NON_CONTEXTUAL_PACKAGE_NAME;
        const groupKey = isNonContextual
            ? node.contextualNode || node.slotOrPackageName
            : node.slotOrPackageName;
        if (!slotGroups.has(groupKey)) slotGroups.set(groupKey, []);
        slotGroups.get(groupKey)!.push(node);
    });
    return slotGroups;
};
